import React from 'react';
import { Clock, ChevronDown, ChevronUp, Undo, Luggage } from 'lucide-react';
import { Task, TaskStatus } from '../../types/todo';
import { TaskPriority } from '../TaskPriority';
import { TaskStatus as TaskStatusComponent } from '../TaskStatus';

interface TaskHeaderProps {
  task: Task;
  isPreview: boolean;
  isCollapsed: boolean;
  onToggleCollapse: (e: React.MouseEvent) => void;
  daysRemaining: number | null;
  onUpdateStatus: (taskId: string, status: TaskStatus) => void;
  onUpdatePriority: (taskId: string, priority: string) => void;
}

export function TaskHeader({
  task,
  isPreview,
  isCollapsed,
  onToggleCollapse,
  daysRemaining,
  onUpdateStatus,
  onUpdatePriority
}: TaskHeaderProps) {
  const [showUndoButton, setShowUndoButton] = React.useState(false);
  const [previousStatus, setPreviousStatus] = React.useState<TaskStatus | null>(null);
  const undoTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Nettoyer le timeout lors du démontage du composant
  React.useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
    };
  }, []);

  const handleStatusClick = () => {
    if (isPreview) return;
    
    // Sauvegarder le statut actuel avant de le changer
    setPreviousStatus(task.status);
    
    // Déterminer le prochain statut
    const nextStatus: TaskStatus = 
      task.status === 'not-started' ? 'in-progress' :
      task.status === 'in-progress' ? 'completed' : 'not-started';
    
    // Mettre à jour le statut
    onUpdateStatus(task.id, nextStatus);
    
    // Afficher le bouton d'annulation
    setShowUndoButton(true);
    
    // Cacher le bouton après 2 secondes
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
    }
    undoTimeoutRef.current = setTimeout(() => {
      setShowUndoButton(false);
      setPreviousStatus(null);
    }, 2000);
  };

  const handleUndo = (e: React.MouseEvent) => {
    e.stopPropagation(); // Empêcher le déclenchement du handleStatusClick
    
    if (previousStatus !== null) {
      // Restaurer le statut précédent
      onUpdateStatus(task.id, previousStatus);
    }
    
    // Masquer le bouton et réinitialiser l'état
    setShowUndoButton(false);
    setPreviousStatus(null);
    
    // Nettoyer le timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }
  };

  const handlePriorityClick = () => {
    if (isPreview) return;
    const nextPriority = 
      task.priority === 'low' ? 'medium' :
      task.priority === 'medium' ? 'high' : 'low';
    onUpdatePriority(task.id, nextPriority);
  };

  const getDaysRemainingStyles = (days: number) => {
    if (days <= 7) {
      return 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400';
    } else if (days <= 30) {
      return 'bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400';
    }
    return 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400';
  };

  const hasDetails = task.description || task.subtasks.length > 0 || task.notes;

  return (
    <div>
      {/* Header with title and badges */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
        {/* Title */}
        <div className="flex-1 min-w-0 order-2 sm:order-1">
          <div className={`text-base sm:text-lg font-medium text-gray-900 dark:text-white ${
            task.status === 'completed' ? 'line-through' : ''
          }`}>
            {task.title}
            {task.subtasks.length > 0 && (
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                ({task.subtasks.filter(st => st.completed).length}/{task.subtasks.length})
              </span>
            )}
          </div>
        </div>

        {/* Status badges */}
        <div className="flex flex-wrap items-center gap-2 order-1 sm:order-2">
          <div className="relative">
            <button
              onClick={handleStatusClick}
              disabled={isPreview}
              className="cursor-pointer"
            >
              <TaskStatusComponent status={task.status} isPreview={isPreview} />
            </button>
            
            {/* Bouton d'annulation */}
            {showUndoButton && !isPreview && (
              <button
                onClick={handleUndo}
                className="absolute -bottom-8 sm:right-0 left-0 sm:left-auto whitespace-nowrap flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded-lg border border-amber-200 dark:border-amber-800/30 shadow-sm hover:bg-amber-100 dark:hover:bg-amber-800/30 transition-colors animate-fade-in z-10"
              >
                <Undo className="w-3 h-3" />
                <span>Annuler</span>
              </button>
            )}
          </div>
          <TaskPriority
            priority={task.priority}
            isPreview={isPreview}
            onClick={handlePriorityClick}
          />
          {daysRemaining !== null && daysRemaining > 0 && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium whitespace-nowrap ${getDaysRemainingStyles(daysRemaining)}`}>
              <Clock className="w-4 h-4" />
              <span>J-{daysRemaining}</span>
            </div>
          )}
        </div>
      </div>

      {/* Toggle button for details - visible only if there are details to show */}
      {hasDetails && (
        <button
          onClick={onToggleCollapse}
          className={`mt-4 w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-all duration-300 transform ${
            isCollapsed 
              ? 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-700/50 text-gray-600 dark:text-gray-400'
              : 'bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/20 dark:hover:bg-primary-900/30 text-primary-600 dark:text-primary-400'
          } border border-gray-200/50 dark:border-gray-700/50 hover:shadow-md`}
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? "Afficher les détails" : "Masquer les détails"}
        >
          {isCollapsed ? (
            <span className="flex items-center gap-2 mx-auto">
              <span>Afficher les détails</span> 
              <ChevronDown className="w-4 h-4" />
            </span>
          ) : (
            <span className="flex items-center gap-2 mx-auto">
              <span>Masquer les détails</span>
              <ChevronUp className="w-4 h-4" />
            </span>
          )}
        </button>
      )}

      {/* Description - now visible when expanded regardless of task status */}
      {task.description && !isCollapsed && (
        <div className="mt-4 border-t dark:border-gray-700/50 pt-4">
          <div 
            className="task-description"
            dangerouslySetInnerHTML={{ __html: task.description }}
          />
        </div>
      )}
    </div>
  );
}